import * as React from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { Box, Button, Paper } from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import "jspdf-autotable";
import { useEffect } from "react";
import { useState } from "react";
import { createTheme, ThemeProvider, styled } from "@mui/material/styles";


const theme = createTheme({
  palette: {
    primary: {
      main: "#003366",
      light: "#1a4d80",
      dark: "#001a33",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#0066cc",
      light: "#3385d6",
      dark: "#004d99",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#003366",
      secondary: "#6c757d",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 4,
        },
      },
    },
  },
});


const Container = styled(Box)({
  padding: "16px 20px",
  backgroundColor: "#ffffff",
});

const CustomFormControl = styled(FormControl)({
  minWidth: 120,
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    color: "#808080",
    fontWeight: 500,
    "&.Mui-focused": {
      color: "#003366",
    },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 4,
    backgroundColor: "#ffffff",
    height: 36,
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#cccccc",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#003366",
      borderWidth: 1,
    },
  },
  "& .MuiSelect-select": {
    fontSize: "0.8rem",
    color: "#003366",
    fontWeight: 500,
    padding: "8px 12px",
  },
});

const CustomMenuItem = styled(MenuItem)({
  fontSize: "0.8rem",
  color: "#003366",
  padding: "6px 14px",
  minHeight: "auto",
  "&:hover": {
    backgroundColor: "rgba(0, 51, 102, 0.05)",
  },
  "&.Mui-selected": {
    backgroundColor: "#f0f4f8",
    "&:hover": {
      backgroundColor: "#e6f0f7",
    },
  },
});

export default function SelectVariants(Props) {
  const {
    setaccountNames,
    accountName,
    region,
    setRegion,
    duration,
    setDuration,
  } = Props;

  let accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [accountDetails, setAccountDetails] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const regionQuery = `
          query GetDetails($id: String!) {
            getDetails(id: $id) {
              region
            }
          }
        `;

        const accountQuery = `
          query MyQuery {
            getAWSAccounts {
              AccountName
              AccountNo
              id
              Status
            }
          }
        `;

        const [regionResponse, accountResponse] = await Promise.all([
          axios.post(
            apiUrl,
            {
              query: regionQuery,
              variables: { id: "1" }
            },
            {
              headers: { Authorization: accessToken }
            }
          ),
          axios.post(
            apiUrl,
            { query: accountQuery },
            {
              headers: { Authorization: accessToken }
            }
          )
        ]);

        const configurationDetails = JSON.parse(regionResponse.request.response);
        const regionjson = JSON.parse(configurationDetails.data.getDetails.region);
        const newRegionOptions = [...Object.keys(regionjson)];
        setRegionOptions(newRegionOptions);
        if (region === "") {
          setSelectedRegion(newRegionOptions[0]);
          setRegion(newRegionOptions[0]);
        } else {
          setSelectedRegion(region);
        }
        const accounts = accountResponse.data.data.getAWSAccounts;

        const activeAccounts = accounts.filter(account => account.Status === 'active');
        setAccountDetails(activeAccounts);
        if (activeAccounts && activeAccounts.length > 0) {
          if (accountName === "") {
            setSelectedAccount(activeAccounts[0].AccountName);
            setaccountNames(activeAccounts[0].AccountName);
          } else {
            setSelectedAccount(accountName);
          }
        }

      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleAccountChange = (event) => {
    const newValue = event.target.value;
    setSelectedAccount(newValue);
    setaccountNames(newValue);
  };

  const handleRegionChange = (event) => {
    const newValue = event.target.value;
    setSelectedRegion(newValue);
    setRegion(newValue);
  };

  const renderSelectValue = (value) => {
    return <span style={{ color: "#003366", fontWeight: 500 }}>{value}</span>;
  };



  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 2, 
          flexWrap: "wrap",
        }}>
          <Box
            sx={{
              display: "flex",
              bgcolor: "#f5f5f5",
              borderRadius: 2,
              p: 0.5,
              mr: 2,
            }}
          >
            {["1 day", "7 days", "30 days"].map((option) => (
              <Button
                key={option}
                onClick={() => setDuration(option)}
                variant={duration === option ? "contained" : "text"}
                size="small"
                sx={{
                  mx: 0.5,
                  textTransform: "none",
                  minWidth: "auto",
                  height: 32,
                  bgcolor: duration === option ? "primary.main" : "transparent",
                  "&:hover": {
                    bgcolor:
                      duration === option
                        ? "primary.dark"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                {option === "1 day"
                  ? "1 Day"
                  : option === "7 days"
                    ? "7 Days"
                    : "30 Days"}
              </Button>
            ))}
          </Box>

          {/* Filter Dropdowns */}
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
            <CustomFormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Account Name</InputLabel>
              <Select
                value={selectedAccount}
                onChange={handleAccountChange}
                label="Account Name"
                renderValue={(value) => renderSelectValue(value.toLowerCase())}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      overflow: 'auto',
                    },
                    sx: {
                      borderRadius: 1,
                      marginTop: 1,
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '2px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                      },
                    },
                  },
                }}
              >
                {Array.isArray(accountDetails) && accountDetails.map((account) => (
                  <CustomMenuItem key={account.AccountName} value={account.AccountName}>
                    {account.AccountName.toLowerCase()}
                  </CustomMenuItem>
                ))}
              </Select>
            </CustomFormControl>

            <CustomFormControl size="small">
              <InputLabel>Region</InputLabel>
              <Select
                value={selectedRegion}
                onChange={handleRegionChange}
                label="Region"
                renderValue={(value) => renderSelectValue(value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      overflow: 'auto',
                    },
                    sx: {
                      borderRadius: 1,
                      marginTop: 1,
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '2px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                      },
                    },
                  },
                }}
              >
                {regionOptions.map((item) => (
                  <CustomMenuItem key={item} value={item}>
                    {item}
                  </CustomMenuItem>
                ))}
              </Select>
            </CustomFormControl>
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
}